<template>
  <main class="creative-settings-page">
    <header>
      <div>
        <h1>创意媒体模型</h1>
        <p>
          每条配置包含模型、平台调用凭据、媒体能力和 Credits 费率，可为同一 Provider 添加多个模型。
        </p>
      </div>
      <a-space>
        <a-button :loading="loading" @click="load"><i class="ri-refresh-line" />刷新</a-button>
        <a-button type="primary" @click="openModel()"
          ><i class="ri-add-line" />新增媒体模型</a-button
        >
      </a-space>
    </header>

    <a-alert
      type="info"
      show-icon
      message="模型与平台账号统一配置"
      description="API Key 等凭据会加密保存；管理员编辑时以密码输入框显示当前值。连接测试会真实调用对应媒体能力，可能产生少量供应商费用。"
    />

    <a-card class="model-list-card" :loading="loading">
      <template #title
        ><span><i class="ri-magic-line" />已配置模型</span></template
      >
      <template #extra
        ><a-tag>{{ visibleModels.length }} 个</a-tag></template
      >
      <a-tabs v-model:active-key="adminKind" class="media-tabs">
        <a-tab-pane key="image" tab="图片模型" />
        <a-tab-pane key="video" tab="视频模型" />
        <a-tab-pane key="audio" tab="语音模型" />
      </a-tabs>
      <a-empty
        v-if="!loading && !visibleModels.length"
        :description="`尚未配置${kindLabel(adminKind)}模型`"
      >
        <a-button type="primary" @click="openModel()"
          ><i class="ri-add-line" />新增媒体模型</a-button
        >
      </a-empty>
      <a-list v-else class="model-list" :data-source="visibleModels">
        <template #renderItem="{ item }">
          <a-list-item class="model-item">
            <a-list-item-meta>
              <template #avatar>
                <span class="model-icon"><i :class="kindIcon(item.mediaKind)" /></span>
              </template>
              <template #title>
                {{ item.name }}
                <a-tag>{{ providerLabel(item.provider) }}</a-tag>
                <a-tag color="blue">{{ kindLabel(item.mediaKind) }}</a-tag>
                <a-tag v-if="item.isDefault" color="processing">默认</a-tag>
                <a-tag v-if="!item.enabled">已停用</a-tag>
              </template>
              <template #description>
                <span class="model-id">{{ item.model }}</span>
                <span class="model-detail">
                  {{ item.operations.map(operationLabel).join(' / ') }} ·
                  <template v-if="item.maxReferenceImages"
                    >最多 {{ item.maxReferenceImages }} 张参考图 ·
                  </template>
                  {{ item.managedCredits }} Credits / 次 ·
                  {{
                    item.hasCredentials || item.provider === 'mock'
                      ? '平台凭据已配置'
                      : '平台凭据未配置'
                  }}
                </span>
              </template>
            </a-list-item-meta>
            <a-space>
              <a-button type="link" :loading="testingId === item.id" @click="testModel(item.id)"
                ><i class="ri-flask-line" />能力测试</a-button
              >
              <a-button type="link" @click="openModel(item)"
                ><i class="ri-edit-line" />编辑</a-button
              >
              <a-popconfirm
                title="确定删除此媒体模型吗？"
                ok-text="删除"
                :ok-button-props="{ danger: true }"
                cancel-text="取消"
                @confirm="removeModel(item.id)"
              >
                <a-button type="link" danger :loading="deletingId === item.id"
                  ><i class="ri-delete-bin-line" />删除</a-button
                >
              </a-popconfirm>
            </a-space>
          </a-list-item>
        </template>
      </a-list>
    </a-card>

    <a-modal
      v-model:open="modelOpen"
      :title="modelForm.id ? '编辑媒体模型' : '新增媒体模型'"
      :footer="null"
      width="760px"
      :closable="!saving"
      :mask-closable="!saving"
      @after-close="resetModel"
    >
      <a-form
        :model="modelForm"
        layout="vertical"
        @finish="saveModel"
        @finish-failed="onFinishFailed"
      >
        <div class="two-columns">
          <a-form-item label="名称" name="name" :rules="required('请输入模型名称')">
            <a-input v-model:value="modelForm.name" placeholder="例如：万相 2.1 文生图" />
          </a-form-item>
          <a-form-item
            label="Provider"
            name="provider"
            :rules="[{ required: true, message: '请选择 Provider' }]"
          >
            <a-select
              v-model:value="modelForm.provider"
              :options="providerOptions"
              :disabled="Boolean(modelForm.id)"
              @change="syncProviderDefaults"
            />
          </a-form-item>
        </div>
        <div class="two-columns">
          <a-form-item label="模型 ID" name="model" :rules="required('请输入模型 ID')">
            <a-input v-model:value="modelForm.model" placeholder="供应商接口使用的模型标识" />
          </a-form-item>
          <a-form-item
            label="媒体类型"
            name="mediaKind"
            :rules="[{ required: true, message: '请选择媒体类型' }]"
          >
            <a-select
              v-model:value="modelForm.mediaKind"
              :options="kindOptions"
              :disabled="Boolean(modelForm.id)"
              @change="syncOperations"
            />
          </a-form-item>
        </div>
        <a-form-item
          label="支持操作"
          name="operations"
          :rules="[{ required: true, type: 'array', min: 1, message: '至少选择一项能力' }]"
        >
          <a-checkbox-group v-model:value="modelForm.operations" :options="availableOperations" />
        </a-form-item>

        <a-divider orientation="left">平台调用账号</a-divider>
        <a-form-item v-if="modelForm.provider === 'aliyun-bailian'" label="地域">
          <a-select v-model:value="modelForm.region" :options="bailianRegionOptions" />
        </a-form-item>
        <a-form-item v-if="modelForm.provider === 'aliyun-bailian'" label="Workspace ID">
          <a-input
            v-model:value="modelForm.workspaceId"
            placeholder="国际地域必填，中国内地按需填写"
          />
        </a-form-item>
        <a-form-item
          v-if="modelForm.provider === 'aliyun-bailian'"
          label="百炼 API Key"
          name="apiKey"
          :rules="required('请输入百炼 API Key')"
        >
          <a-input-password v-model:value="modelForm.apiKey" autocomplete="new-password" />
        </a-form-item>
        <a-form-item
          v-if="modelForm.provider === 'volcengine' && modelForm.mediaKind !== 'audio'"
          label="Ark API Key"
          name="apiKey"
          :rules="required('请输入 Ark API Key')"
        >
          <a-input-password v-model:value="modelForm.apiKey" autocomplete="new-password" />
        </a-form-item>
        <template v-if="modelForm.provider === 'volcengine' && modelForm.mediaKind === 'audio'">
          <a-form-item label="豆包语音 App ID" name="appId" :rules="required('请输入 App ID')">
            <a-input v-model:value="modelForm.appId" />
          </a-form-item>
          <a-form-item
            label="豆包语音 Access Token"
            name="accessToken"
            :rules="required('请输入 Access Token')"
          >
            <a-input-password v-model:value="modelForm.accessToken" autocomplete="new-password" />
          </a-form-item>
          <a-form-item
            label="豆包语音 Resource ID"
            name="resourceId"
            :rules="required('请输入 Resource ID')"
          >
            <a-input v-model:value="modelForm.resourceId" />
          </a-form-item>
        </template>
        <a-form-item label="结果下载允许域名">
          <a-select
            v-model:value="modelForm.resultHostAllowlist"
            mode="tags"
            placeholder="例如 aliyuncs.com"
          />
        </a-form-item>

        <a-divider orientation="left">能力与计费</a-divider>
        <div class="two-columns">
          <a-form-item
            label="平台 Credits / 次"
            name="managedCredits"
            :rules="[
              { required: true, type: 'number', min: 0, message: '请输入不小于 0 的 Credits' },
            ]"
          >
            <a-input-number
              v-model:value="modelForm.managedCredits"
              :min="0"
              :precision="3"
              :step="0.001"
            />
          </a-form-item>
          <a-form-item
            v-if="modelForm.operations.some((operation) => operation.startsWith('image_to_'))"
            label="最多参考图片"
            name="maxReferenceImages"
            :rules="[{ required: true, type: 'number', min: 1, max: 10, message: '请输入 1 至 10' }]"
          >
            <a-input-number v-model:value="modelForm.maxReferenceImages" :min="1" :max="10" />
          </a-form-item>
          <div class="model-switches">
            <label><a-switch v-model:checked="modelForm.enabled" /><span>启用</span></label>
            <label
              ><a-switch v-model:checked="modelForm.isDefault" /><span
                >设为该媒体类型默认模型</span
              ></label
            >
          </div>
        </div>
        <a-form-item label="参数能力 JSON">
          <a-textarea v-model:value="modelForm.parameterSchemaText" :rows="6" />
        </a-form-item>
        <a-form-item label="默认参数 JSON">
          <a-textarea v-model:value="modelForm.defaultParametersText" :rows="4" />
        </a-form-item>
        <footer class="actions">
          <a-button :disabled="saving" @click="modelOpen = false">取消</a-button>
          <a-button type="primary" html-type="submit" :loading="saving"
            ><i class="ri-save-line" />保存模型</a-button
          >
        </footer>
      </a-form>
    </a-modal>
  </main>
</template>

<script setup lang="ts">
import { computed, onMounted, reactive, ref } from 'vue';
import { message } from 'ant-design-vue';
import type { MediaKind, MediaModelAdminView, MediaOperation, MediaProvider } from 'shared';
import { adminApi } from '@/api';

const models = ref<MediaModelAdminView[]>([]);
const adminKind = ref<MediaKind>('image');
const loading = ref(false);
const saving = ref(false);
const deletingId = ref<string | null>(null);
const testingId = ref<string | null>(null);
const modelOpen = ref(false);
const providerOptions = [
  { value: 'aliyun-bailian', label: '阿里云百炼' },
  { value: 'volcengine', label: '火山引擎' },
  { value: 'mock', label: 'Mock（仅测试）' },
];
const kindOptions = [
  { value: 'image', label: '图片' },
  { value: 'video', label: '视频' },
  { value: 'audio', label: '语音' },
];
const operationsByKind: Record<MediaKind, Array<{ value: MediaOperation; label: string }>> = {
  image: [
    { value: 'text_to_image', label: '文生图' },
    { value: 'image_to_image', label: '图生图' },
  ],
  video: [
    { value: 'text_to_video', label: '文生视频' },
    { value: 'image_to_video', label: '图生视频' },
  ],
  audio: [{ value: 'text_to_speech', label: '文本转语音' }],
};
const defaults = () => ({
  id: '',
  name: '',
  provider: 'aliyun-bailian' as MediaProvider,
  model: '',
  mediaKind: 'image' as MediaKind,
  operations: ['text_to_image'] as MediaOperation[],
  region: 'cn-beijing',
  workspaceId: '',
  apiKey: '',
  appId: '',
  accessToken: '',
  resourceId: '',
  resultHostAllowlist: ['aliyuncs.com'] as string[],
  parameterSchemaText: '{\n  "type": "object",\n  "properties": {}\n}',
  defaultParametersText: '{}',
  managedCredits: 0,
  maxReferenceImages: 0,
  enabled: true,
  isDefault: false,
});
const modelForm = reactive(defaults());
const availableOperations = computed(() => operationsByKind[modelForm.mediaKind]);
const visibleModels = computed(() =>
  models.value.filter((item) => item.mediaKind === adminKind.value),
);
const bailianRegionOptions = [
  { value: 'cn-beijing', label: '中国内地（北京）' },
  { value: 'singapore', label: '新加坡' },
  { value: 'us', label: '美国（弗吉尼亚）' },
];
const required = (prompt: string) => [{ required: true, whitespace: true, message: prompt }];

async function load() {
  loading.value = true;
  try {
    models.value = await adminApi.get<MediaModelAdminView[]>('/admin/creative-center/models');
  } catch {
    message.error('创意媒体模型加载失败');
  } finally {
    loading.value = false;
  }
}
function openModel(item?: MediaModelAdminView) {
  const credentials = item?.credentials ?? {};
  Object.assign(
    modelForm,
    defaults(),
    item
      ? {
          ...item,
          id: item.id,
          region: item.region ?? '',
          workspaceId: credentials.workspaceId ?? '',
          apiKey: credentials.apiKey ?? credentials.arkApiKey ?? '',
          appId: credentials.appId ?? '',
          accessToken: credentials.accessToken ?? '',
          resourceId: credentials.resourceId ?? '',
          parameterSchemaText: JSON.stringify(item.parameterSchema, null, 2),
          defaultParametersText: JSON.stringify(item.defaultParameters, null, 2),
          maxReferenceImages: item.maxReferenceImages,
        }
        : {
          mediaKind: adminKind.value,
          operations: operationsByKind[adminKind.value].map((operation) => operation.value),
          maxReferenceImages: adminKind.value === 'audio' ? 0 : 1,
        },
  );
  modelOpen.value = true;
}
function resetModel() {
  Object.assign(modelForm, defaults());
}
function syncOperations() {
  modelForm.operations = operationsByKind[modelForm.mediaKind].map((item) => item.value);
  modelForm.maxReferenceImages = modelForm.operations.some((operation) => operation.startsWith('image_to_'))
    ? Math.max(1, modelForm.maxReferenceImages)
    : 0;
}
function syncProviderDefaults() {
  if (modelForm.provider === 'aliyun-bailian' && !modelForm.region) modelForm.region = 'cn-beijing';
  modelForm.resultHostAllowlist =
    modelForm.provider === 'aliyun-bailian'
      ? ['aliyuncs.com']
      : modelForm.provider === 'volcengine'
        ? ['volces.com', 'byteimg.com', 'volccdn.com']
        : [];
}
async function saveModel() {
  saving.value = true;
  try {
    const parameterSchema = JSON.parse(modelForm.parameterSchemaText);
    const defaultParameters = JSON.parse(modelForm.defaultParametersText);
    await adminApi.post('/admin/creative-center/models', {
      id: modelForm.id || undefined,
      name: modelForm.name,
      provider: modelForm.provider,
      model: modelForm.model,
      mediaKind: modelForm.mediaKind,
      operations: modelForm.operations,
      region: modelForm.region,
      credentials:
        modelForm.provider === 'aliyun-bailian'
          ? { apiKey: modelForm.apiKey, workspaceId: modelForm.workspaceId }
          : modelForm.mediaKind === 'audio'
            ? {
                appId: modelForm.appId,
                accessToken: modelForm.accessToken,
                resourceId: modelForm.resourceId,
              }
            : { arkApiKey: modelForm.apiKey },
      resultHostAllowlist: modelForm.resultHostAllowlist,
      parameterSchema,
      defaultParameters,
      managedCredits: modelForm.managedCredits,
      maxReferenceImages: modelForm.maxReferenceImages,
      enabled: modelForm.enabled,
      isDefault: modelForm.isDefault,
    });
    modelOpen.value = false;
    await load();
    message.success('媒体模型已保存');
  } catch (error) {
    message.error(
      error instanceof SyntaxError ? '参数 JSON 格式不正确' : '媒体模型保存失败，请检查模型与凭据',
    );
  } finally {
    saving.value = false;
  }
}
async function testModel(id: string) {
  testingId.value = id;
  try {
    const result = await adminApi.post<{ message: string }>(
      `/admin/creative-center/models/${id}/test`,
      {},
    );
    message.success(result.message);
  } catch {
    message.error('能力测试失败，请检查模型 ID、凭据和媒体能力');
  } finally {
    testingId.value = null;
  }
}
async function removeModel(id: string) {
  deletingId.value = id;
  try {
    await adminApi.delete(`/admin/creative-center/models/${id}`);
    await load();
    message.success('媒体模型已删除');
  } catch {
    message.error('删除失败，请确认没有进行中的生成任务');
  } finally {
    deletingId.value = null;
  }
}
function onFinishFailed() {
  message.warning('请先补全必填项');
}
function providerLabel(provider: MediaProvider) {
  return { 'aliyun-bailian': '百炼', volcengine: '火山', mock: 'Mock' }[provider];
}
function kindLabel(kind: MediaKind) {
  return { image: '图片', video: '视频', audio: '语音' }[kind];
}
function kindIcon(kind: MediaKind) {
  return kind === 'image'
    ? 'ri-image-2-line'
    : kind === 'video'
      ? 'ri-movie-2-line'
      : 'ri-volume-up-line';
}
function operationLabel(operation: MediaOperation) {
  return {
    text_to_image: '文生图',
    image_to_image: '图生图',
    text_to_video: '文生视频',
    image_to_video: '图生视频',
    text_to_speech: '文本转语音',
  }[operation];
}

onMounted(() => {
  void load();
});
</script>

<style scoped>
.creative-settings-page {
  max-width: 1180px;
  margin: 0 auto;
  padding: 32px;
}
.creative-settings-page > header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 20px;
}
.creative-settings-page h1 {
  margin: 0 0 7px;
  color: var(--aw-text);
}
.creative-settings-page header p {
  max-width: 760px;
  margin: 0;
  color: var(--aw-text-secondary);
}
.model-list-card {
  margin-top: 18px;
}
.model-list-card :deep(.ant-card-head-title) > span {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}
.model-item {
  gap: 16px;
}
.model-icon {
  display: grid;
  width: 38px;
  height: 38px;
  place-items: center;
  border-radius: var(--aw-radius-sm);
  color: var(--aw-primary);
  background: var(--aw-primary-soft);
  font-size: 19px;
}
.model-id,
.model-detail {
  display: block;
}
.model-id {
  color: var(--aw-text);
  font-family: ui-monospace, SFMono-Regular, Consolas, monospace;
}
.model-detail {
  margin-top: 4px;
  color: var(--aw-text-secondary);
}
.two-columns {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
}
.model-switches {
  display: flex;
  align-items: center;
  gap: 22px;
  padding-top: 30px;
}
.model-switches label {
  display: flex;
  align-items: center;
  gap: 8px;
  color: var(--aw-text-secondary);
}
.actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 8px;
}
@media (max-width: 760px) {
  .creative-settings-page {
    padding: 20px;
  }
  .creative-settings-page > header,
  .two-columns {
    display: grid;
    grid-template-columns: 1fr;
  }
  .model-item {
    align-items: flex-start;
    flex-direction: column;
  }
  .model-switches {
    padding-top: 0;
    flex-wrap: wrap;
  }
}
</style>
